/**
 * Bytes of a PDF → the index this app keeps of it.
 *
 * There is one of these because there are two ways a book arrives — somebody chooses a file, or
 * the app installs a chapter it shipped with — and the plan's rule for the second is that it takes
 * **the same path as an upload, with no special case**: if the built-in book needs its own reader,
 * the reader has a bug. So both callers land here, and the only thing that differs between them is
 * a `builtin` flag on the record.
 *
 * ── The parser may fail; opening the book may not ──────────────────────────────────────────
 *
 * Finding exercise numbers and the facit is a GUESS over somebody else's typesetting, and on a
 * book it has never seen it can throw. It used to take the whole upload down with it — the owner,
 * twice: *"I can not upload it to my book thing"*. The pages ARE the book; the index is a
 * convenience on top of them, so its failure costs the click regions and the facit, never the
 * chapter. `degraded` says which happened.
 */
import type { DocExercise, DocIndex } from './book-pdf';
import { parseChapter } from './ingest';
import { extractPdf } from './pdf';
import { regionsFor, resolutionOf } from './pdf-regions';

export interface IngestRequest {
  /** The whole file. Not transferred: `extractPdf` detaches what it is handed. */
  bytes: ArrayBuffer;
  /** What to call it in the library. A filename, usually. */
  name: string;
  chapterId: string;
  courseId?: string;
  /** Given for a built-in chapter, so reinstalling replaces rather than duplicates. */
  id?: string;
  builtin?: true;
  onProgress?: (page: number, pages: number) => void;
}

export interface IngestResult {
  index: DocIndex;
  /** True when the pages are readable but the exercise index could not be built. */
  degraded: boolean;
}

export async function ingestPdf(request: IngestRequest): Promise<IngestResult> {
  // `extractPdf` transfers its buffer into the worker, so the copy that goes to the database has
  // to be taken before that — a detached buffer stored here is a book that can never be opened.
  const extraction = await extractPdf(
    new Blob([request.bytes.slice(0)], { type: 'application/pdf' }),
    { onProgress: request.onProgress },
  );

  let parsed: ReturnType<typeof parseChapter> = { exercises: [], facitAt: null };
  let found: ReturnType<typeof regionsFor> = [];
  let resolution = { resolved: 0, total: 0 };
  let degraded = false;
  try {
    parsed = parseChapter(extraction.text);
    found = regionsFor(parsed.exercises, extraction.placed);
    resolution = resolutionOf(parsed.exercises, found);
  } catch (error) {
    // Not a failure the reader has to hear about: nothing stops them reading. The console keeps
    // the real stack, which is the only thing that names the line.
    console.error('The exercise index could not be built for this PDF', error);
    degraded = true;
    parsed = { exercises: [], facitAt: null };
    found = [];
    resolution = { resolved: 0, total: 0 };
  }

  /*
   * The FIRST page a number is printed on, not the last.
   *
   * `new Map(found.map(...))` kept the last entry for a repeated key, and `found` is sorted
   * ascending by page — so an exercise spanning two pages recorded the second, and a number the
   * parser also matched inside the Facit recorded the facit's page. Jumping to 1042 then landed on
   * the answers instead of the question. The owner: *"the search thing which takes you to a task …
   * now it takes you to either facit or the task, it has to be to the task."*
   */
  const firstPage = new Map<number, number>();
  for (const region of found) {
    const held = firstPage.get(region.n);
    if (held === undefined || region.page < held) firstPage.set(region.n, region.page);
  }

  return {
    degraded,
    index: {
      id: request.id ?? `pdf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      name: request.name,
      chapterId: request.chapterId,
      courseId: request.courseId ?? '',
      pageCount: extraction.pageCount,
      pageSizes: extraction.pageSizes,
      addedAt: Date.now(),
      exercises: parsed.exercises.map<DocExercise>(exercise => ({
        n: exercise.n,
        level: exercise.level,
        starred: exercise.starred,
        answer: exercise.answer,
        page: firstPage.get(exercise.n) ?? 0,
      })),
      regions: found,
      resolved: resolution.resolved,
      total: resolution.total,
      ...(request.builtin ? { builtin: true as const } : {}),
    },
  };
}
